import { createApi } from '@reduxjs/toolkit/query/react'

import { selectStoreId } from '../features/app/selectors'
import { staggeredBaseQueryWithBailOut } from '../libs/services'
import { Exam, ExamApi, ExamQuery, StrippedExam } from '../model/exam'
import { useTeloSelector } from '../store'
import examsActions from '../features/exams/actions'

import { unversionedApiUrl } from './common'
import { serviceApi } from './serviceApi'
import {
	CheckInExamQueryArg,
	GetExamsQueryArg,
	LockExamQueryArg,
	UseGetExamsQueryOptions,
} from './model'
import { ServicesReducerPath } from './constants'

export const examsApi = createApi({
	reducerPath: ServicesReducerPath.ExamsApi,
	baseQuery: staggeredBaseQueryWithBailOut(`${unversionedApiUrl}`),
	endpoints: builder => ({
		getExams: builder.query<Exam[], GetExamsQueryArg>({
			query: ({ query, storeId }) => ({
				url: serviceApi.getExams(query),
				headers: {
					'x-store': storeId,
				},
			}),
			// * clear cache when the last component that uses this query unmounts
			keepUnusedDataFor: 0,
		}),
		checkInExam: builder.mutation<StrippedExam | ExamApi, CheckInExamQueryArg>({
			query: ({ examId, ...payload }) => ({
				url: serviceApi.checkInExam(examId),
				method: 'PUT',
				body: JSON.stringify(payload),
			}),
			async onQueryStarted(_, { dispatch, queryFulfilled }) {
				try {
					const { data: exam } = await queryFulfilled

					dispatch(examsActions._loadExam(exam))
				} catch {}
			},
		}),

		lockExam: builder.mutation<boolean, LockExamQueryArg>({
			query: ({ examId }) => ({
				url: serviceApi.lockExam(examId),
				method: 'PATCH',
				body: JSON.stringify({}),
			}),
		}),
	}),
})

export const useGetExamsQuery = (
	query: ExamQuery,
	options?: UseGetExamsQueryOptions,
) => {
	const storeId = useTeloSelector(selectStoreId)
	return examsApi.useGetExamsQuery({ query, storeId }, options)
}

export const { useCheckInExamMutation, useLockExamMutation } = examsApi
